import React, { useState } from 'react';
import { Button, Input } from 'antd';
import { MenuFoldOutlined, SearchOutlined } from '@ant-design/icons';
import type { NavigateFunction } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { gql } from '@apollo/client';
import { datadogRum } from '@datadog/browser-rum';
import { CreateDropdownButton } from './CreateDropdownButton';
import { ModuleSwitcher, getActiveModuleKey } from './ModuleSwitcher';
import { useFilteredSites } from './trees/shared';
import { MonitorTree } from './trees/MonitorTree';
import { StudioTree } from './trees/StudioTree';
import { DevicesTree } from './trees/DevicesTree';

interface SidebarMenuProps {
  onSidebarOpenClose: (close: boolean) => void;
  sidebarOpen: boolean;
  search: string;
  pathname: string;
  navigate: NavigateFunction;
  /** When false, the All Spaces tree is not rendered. Used to stagger the
   * reveal: during the 300ms expand animation the top (search + module
   * switcher) shows first and the heavy tree mounts after, avoiding the
   * brief pill/Collapse reflow inside a still-growing container. Defaults
   * to true so callers that don't care get the full menu. */
  showTree?: boolean;
}

/**
 * The sidebar's All Spaces section. Renders the shared chrome (fold button,
 * search input, module switcher) and dispatches to a per-module Tree
 * component for the actual space picker.
 *
 * Each Tree (`MonitorTree`, `StudioTree`, `DevicesTree`) owns its own state
 * and click handlers — they don't share `selectedKey`, `openMenuKeys`, or
 * any module-conditional logic. Switching modules unmounts one Tree and
 * mounts the next, which means each module's behaviour can evolve without
 * accidentally regressing the others.
 */
const SidebarMenu: React.FC<SidebarMenuProps> = ({
  onSidebarOpenClose,
  sidebarOpen,
  search,
  pathname,
  navigate,
  showTree = true,
}) => {
  const module = getActiveModuleKey(pathname);
  const [filterValue, setFilterValue] = useState('');
  const { sites, error: siteError } = useFilteredSites(filterValue);

  // The default-space redirect (Monitor/Studio empty URL → first floor)
  // lives in DefaultSpaceRedirector, mounted unconditionally in Sidebar.tsx
  // so it fires regardless of collapsed/expanded state.

  const handleToggleSidebar = () => onSidebarOpenClose(true);

  const treeBody = (() => {
    if (siteError) {
      return (
        <div className="px-4 py-3 text-xs text-content-secondary">
          Failed to load spaces. Please try refreshing.
        </div>
      );
    }
    if (filterValue && (sites?.length ?? 0) === 0) {
      return <NullSearch />;
    }
    if (!sites) return null;
    const treeProps = {
      sites,
      filterValue,
      search,
      navigate,
    };
    if (module === 'studio') return <StudioTree {...treeProps} />;
    if (module === 'devices') return <DevicesTree {...treeProps} />;
    return <MonitorTree {...treeProps} />;
  })();

  return (
    <>
      <div className="flex gap-2 px-1 pb-2 items-center">
        <Button
          type="text"
          shape="circle"
          icon={<MenuFoldOutlined />}
          onClick={handleToggleSidebar}
          aria-label="Collapse sidebar"
          data-id="sidebar-toggle"
        />
        <Input
          data-id="search-input"
          placeholder="Search"
          prefix={<SearchOutlined className="text-content-tertiary" />}
          allowClear
          value={filterValue}
          onFocus={() => {
            datadogRum.addAction('Activate new sidebar search', {
              source: 'sites-and-buildings',
            });
          }}
          onChange={(ev) => setFilterValue(ev.target.value)}
          style={{ borderRadius: 9999 }}
        />{' '}
        {!sidebarOpen && <CreateDropdownButton variant="compact" />}
      </div>

      {sidebarOpen && <ModuleSwitcher />}

      {sidebarOpen && showTree && <hr className="border-t border-[#d9d9d9] my-2 mx-1" />}

      {sidebarOpen && showTree && treeBody}

      {!sidebarOpen && treeBody}
    </>
  );
};

export default React.memo(SidebarMenu);

export function NullSearch() {
  const { t } = useTranslation();
  return (
    <div className="flex justify-center items-center">
      <div className="flex flex-col items-center gap-2 px-3 py-2 w-[216px]">
        <span className="text-xs font-semibold">{t('noResults')}</span>
        <span className="text-[10px] text-center">{t('searchAction')}</span>
      </div>
    </div>
  );
}

// ============================================================================
// GraphQL fragments — kept in this file because callers import them from here.
// ============================================================================

export const SidebarMenuZoneFragment = gql`
  fragment SidebarMenuZone on Zone {
    id: zone_id
    name
    floorId: floor_id
    roomId: room_id
  }
`;

export const SidebarMenuRoomFragment = gql`
  fragment SidebarMenuRoom on Room {
    id: room_id
    name
    floorId: floor_id
  }
`;
export const SidebarMenuFloorFragment = gql`
  fragment SidebarMenuFloor on Floor {
    id: floor_id
    name
    rooms {
      id: room_id
      ...SidebarMenuRoom
    }
    zones {
      id: zone_id
      ...SidebarMenuZone
    }
  }
  ${SidebarMenuRoomFragment}
  ${SidebarMenuZoneFragment}
`;

export const SidebarMenuBuildingFragment = gql`
  fragment SidebarMenuBuilding on Building {
    id
    name
    floors {
      id: floor_id
      capacity {
        max
      }
      ...SidebarMenuFloor
    }
  }
  ${SidebarMenuFloorFragment}
`;

export const SidebarMenuSiteFragment = gql`
  fragment SidebarMenuSite on Site {
    id
    name
    buildings {
      id
      ...SidebarMenuBuilding
    }
  }
  ${SidebarMenuBuildingFragment}
`;

// NOT dead code — the `gql` template defines the `SidebarFloor` operation
// that codegen turns into `SidebarFloorDocument`, which `CreateFloorButton`
// and `DeleteSpaceButton` use as `refetchQueries` after mutations. The
// `export` keyword is retained so the variable isn't flagged as unused;
// codegen scans `gql` template literals regardless of export status.
export const SidebarMenuFloorQuery = gql`
  query SidebarFloor {
    floors {
      data {
        id: floor_id
        ...SidebarMenuFloor
      }
    }
  }
  ${SidebarMenuFloorFragment}
`;

export const SidebarMenuSiteQuery = gql`
  query SidebarSite {
    sites {
      data {
        id
        ...SidebarMenuSite
      }
    }
  }
  ${SidebarMenuSiteFragment}
`;
